import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Button } from '../ui'
import { ChevronDown, User as UserIcon, MessageCircle, LogOut, Gift, Check, Plus } from 'lucide-react'
import { APP_ROUTES } from '../constants'
import { useLocation, useNavigate } from 'react-router-dom'
import { AccountType, User, FeaturePromotion, Account } from '../types'
import { analytics } from '../analytics/analytics'
import { useState, useCallback, memo, useRef, useEffect, useMemo } from 'react'
import { TooltipProvider, Tooltip, TooltipTrigger } from '@/common/ui/tooltip'
import useAccountStore from '@/account/accountStore'
import useAccountSwitcher from '@/account/useAccountSwitcher'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { useSignOut } from '@/auth/useSignOut'
import { NAVIGATION_COPY } from '@/locales/en'
import { useFeaturePromotionViewed } from '@/user/userQueries'
import AddAccountModal from '@/application/addAccountModal'
import TrustAccountsPromo from './TrustAccountsPromo'
import { ProfileDropdownAddIndividualAccountClickedAnalytics } from '@/common/analytics/events/ProfileDropdownAddIndividualAccountClickedAnalytics'
import { getAccountHomeRoute } from './accountRoutes'

type Props = {
    user: User | null
    rewardsCount: number
    activeAccountId: string | null
}

const HARD_ACCOUNT_LIMIT = 5

const AccountLabel = memo(function AccountLabel(props: {
    name: string
    type: AccountType
    active: boolean
    accountId?: string
}) {
    return (
        <div className="flex-1 flex justify-start items-center gap-2">
            <div className="flex-1 flex flex-col justify-center items-start min-w-0">
                <div className="self-stretch justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                    {props.name}
                </div>

                <div className="self-stretch inline-flex justify-start items-center gap-1">
                    <div className="justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                        {NAVIGATION_COPY.accountTypes[props.type]}
                    </div>

                    {props.accountId && (
                        <>
                            <div className="w-1 h-1 bg-content-secondary rounded-full" />
                            <div className="justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                                {props.accountId}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="w-6 h-6 relative overflow-hidden">
                {props.active && (
                    <span className="w-6 h-6 rounded-full flex items-center justify-center bg-brand-primary-forest">
                        <Check size={14} strokeWidth={3} className="text-white" />
                    </span>
                )}
            </div>
        </div>
    )
})

export default function ProfileDropdown(props: Props) {
    const { user, rewardsCount, activeAccountId } = props
    const accounts = useAccountStore((s) => s.getAccounts())
    const primaryAccount = usePrimaryAccount()
    const switchAccount = useAccountSwitcher()
    const featurePromotionViewed = useFeaturePromotionViewed()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const signOut = useSignOut()
    const [isOpen, setIsOpen] = useState(false)
    const [isNewAccountOpen, setIsNewAccountOpen] = useState(false)
    const [newAccountInitialType, setNewAccountInitialType] = useState<AccountType | undefined>()
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const [showTrustPromo, setShowTrustPromo] = useState(false)

    useEffect(() => {
        if (user?.featurePromotionFlags?.[FeaturePromotion.TRUST_ACCOUNTS] !== true) return
        setShowTrustPromo(true)
    }, [user?.featurePromotionFlags, setShowTrustPromo])

    const handleSwitch = useCallback(
        async (accountId: string, accountType: AccountType) => {
            const homeRoute = getAccountHomeRoute(accountType)

            if (accountId === activeAccountId && pathname === homeRoute) return
            setIsOpen(false)
            analytics.trackWithDefaults('profile_dropdown_account_selected', { accountId, account_type: accountType })

            if (pathname !== homeRoute) {
                navigate(homeRoute)
            }

            if (accountId !== activeAccountId) {
                await switchAccount(accountId)
            }
        },
        [activeAccountId, pathname, switchAccount, navigate],
    )

    const signOutHandler = async () => {
        try {
            setIsOpen(false)
            analytics.trackWithDefaults('profile_dropdown_logout_selected')

            await signOut({ redirect: true })
        } catch (error) {
            analytics.track('Sign Out Failed', { error: error instanceof Error ? error.message : 'Unknown error' })
            // Fallback: force redirect if sign out fails
            window.location.href = '/'
        }
    }

    const trackOpenChange = (open: boolean) => {
        dismissTrustPromo()
        setIsOpen(open)
        if (open)
            analytics.trackWithDefaults(`profile_dropdown_opened`, {
                notification: rewardsCount > 0,
            })
    }

    const individualName = `${user?.firstName ?? ''} ${user?.lastName?.[0] ?? ''}`.trim()
    const activeAccount =
        (primaryAccount?.accountId === activeAccountId ? primaryAccount : undefined) ||
        accounts.find((acc) => acc.accountId === activeAccountId) ||
        null
    const activeProfileName = activeAccount?.accountName || individualName
    const activeProfileType = activeAccount?.accountType ?? user?.accountType ?? AccountType.INDIVIDUAL
    const avatarContent = useMemo(() => {
        const source = activeProfileName?.trim()
        if (!source) return ' '

        const parts = source.split(/\s+/).filter(Boolean)
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

        return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
    }, [activeProfileName])

    const visibleAccounts = useMemo(() => {
        const remainingAccounts = primaryAccount
            ? accounts.filter((account) => account.accountId !== primaryAccount.accountId)
            : accounts

        return remainingAccounts.slice(0, HARD_ACCOUNT_LIMIT - (primaryAccount ? 1 : 0))
    }, [accounts, primaryAccount])

    const dismissTrustPromo = useCallback(() => {
        if (!showTrustPromo) return
        featurePromotionViewed.mutate(FeaturePromotion.TRUST_ACCOUNTS)
        setShowTrustPromo(false)
    }, [featurePromotionViewed, setShowTrustPromo, showTrustPromo])

    return (
        <>
            <Popover open={isOpen} onOpenChange={trackOpenChange}>
                <TooltipProvider>
                    <Tooltip
                        open={showTrustPromo}
                        enableOverlay={true}
                        onOpenChange={(open) => {
                            if (!open) dismissTrustPromo()
                        }}
                    >
                        <TooltipTrigger asChild>
                            <PopoverTrigger asChild>
                                <Button
                                    ref={buttonRef}
                                    variant="ghost"
                                    className={`h-[42px] pl-1 pr-2 py-1 gap-3 border-0 ${isOpen ? 'bg-brand-dust' : ''} relative z-50`}
                                    aria-label="Open profile menu"
                                    aria-haspopup="menu"
                                >
                                    <div className="inline-flex justify-start items-center gap-3">
                                        <div className="h-8 w-8 bg-brand-forest text-white rounded-full flex items-center justify-center relative">
                                            {avatarContent}
                                            {rewardsCount > 0 && (
                                                <span
                                                    className="absolute left-6 top-6 w-2 h-2 bg-brand-emerald rounded-full border border-white"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </div>
                                        <div className="hidden md:flex w-24 justify-start items-center gap-1">
                                            <div className="flex-1 inline-flex flex-col justify-center items-start">
                                                <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                                                    {activeProfileName}
                                                </div>
                                                <div className="h-3.5 justify-center text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                                                    {NAVIGATION_COPY.accountTypes[activeProfileType]}
                                                </div>
                                            </div>
                                            <div className="w-4 h-4 relative overflow-hidden">
                                                <ChevronDown size={16} />
                                            </div>
                                        </div>
                                    </div>
                                </Button>
                            </PopoverTrigger>
                        </TooltipTrigger>

                        {/* set `promoEnableOverlay` to false to disable page overlay */}
                        <TrustAccountsPromo onDismiss={dismissTrustPromo} />
                    </Tooltip>
                </TooltipProvider>
                <PopoverContent
                    className="w-full md:w-72 max-w-[calc(100vw)] px-2 py-4 inline-flex flex-col justify-start items-start"
                    align="end"
                    mobileAnimate={true}
                >
                    <div className="self-stretch px-2 inline-flex justify-center items-center gap-2.5 mb-4">
                        <div className="flex-1 justify-start text-black text-3xl font-normal font-['Serious_Sans'] leading-10">
                            {NAVIGATION_COPY.profile}
                        </div>
                    </div>

                    <ul className="self-stretch w-full space-y-2" role="menu" aria-label="Profile menu">
                        {primaryAccount ? (
                            <li key={primaryAccount.accountId} className="w-full">
                                <button
                                    type="button"
                                    role="menuitem"
                                    aria-current={primaryAccount.accountId === activeAccountId ? 'true' : undefined}
                                    disabled={
                                        primaryAccount.accountType === AccountType.ADVISER
                                            ? false
                                            : primaryAccount.accountId === activeAccountId
                                    }
                                    onClick={() => handleSwitch(primaryAccount.accountId, primaryAccount.accountType)}
                                    className="w-full py-1.5 px-2 flex items-center hover:bg-accent rounded text-left"
                                >
                                    <AccountLabel
                                        name={primaryAccount.accountName || individualName}
                                        type={primaryAccount.accountType}
                                        accountId={primaryAccount.accountId}
                                        active={primaryAccount.accountId === activeAccountId}
                                    />
                                </button>
                            </li>
                        ) : (
                            <li className="w-full">
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setIsOpen(false)
                                        ProfileDropdownAddIndividualAccountClickedAnalytics.track()
                                        setNewAccountInitialType(AccountType.INDIVIDUAL)
                                        setIsNewAccountOpen(true)
                                    }}
                                    className="w-full py-1.5 px-2 flex items-center hover:bg-accent rounded text-left gap-2"
                                >
                                    <div className="flex-1 inline-flex flex-col justify-center items-start">
                                        <div className="self-stretch justify-start text-content-primary text-sm font-normal font-['Inter'] leading-5">
                                            {NAVIGATION_COPY.addIndividualTitle}
                                        </div>
                                        <div className="justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                                            {NAVIGATION_COPY.addIndividualDescription}
                                        </div>
                                    </div>
                                    <Plus size={16} />
                                </button>
                            </li>
                        )}
                        <div role="separator" aria-hidden="true" className="w-full h-px bg-muted" />
                        {visibleAccounts.map((acc: Account) => {
                            const isActive = acc.accountId === activeAccountId
                            return (
                                <li key={acc.accountId} className="w-full">
                                    <button
                                        type="button"
                                        role="menuitem"
                                        aria-current={isActive ? 'true' : undefined}
                                        disabled={isActive}
                                        onClick={() => handleSwitch(acc.accountId, acc.accountType)}
                                        className="w-full py-1.5 px-2 flex items-center hover:bg-accent rounded text-left"
                                    >
                                        <AccountLabel
                                            name={acc.accountName || individualName}
                                            type={acc.accountType}
                                            accountId={acc.accountId}
                                            active={isActive}
                                        />
                                    </button>
                                </li>
                            )
                        })}
                        <div role="separator" aria-hidden="true" className="w-full h-px bg-muted" />
                        {accounts.length < HARD_ACCOUNT_LIMIT && (
                            <li className="w-full">
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setIsOpen(false)
                                        analytics.trackWithDefaults('profile_dropdown_add_account_clicked')
                                        setNewAccountInitialType(undefined)
                                        setIsNewAccountOpen(true)
                                    }}
                                    className="w-full py-1.5 px-2 flex items-center hover:bg-accent rounded text-left"
                                >
                                    <div className="flex justify-start items-center gap-2">
                                        <Plus size={16} />
                                        <div className="justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                                            {NAVIGATION_COPY.addAccount}
                                        </div>
                                    </div>
                                </button>
                            </li>
                        )}

                        <li className="mt-4">
                            <div
                                className="w-full px-2 text-content-secondary text-sm font-medium font-['Inter'] leading-5"
                                role="heading"
                                aria-level={2}
                            >
                                {NAVIGATION_COPY.manage}
                            </div>
                        </li>

                        <li className="w-full">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setIsOpen(false)
                                    analytics.trackWithDefaults('profile_dropdown_item_selected', {
                                        item: 'account_settings',
                                    })
                                    navigate(APP_ROUTES.PROFILE)
                                }}
                                className="w-full py-1.5 px-2 gap-2 flex items-center hover:bg-accent rounded text-sm text-left"
                            >
                                <UserIcon size={16} />
                                <div>{NAVIGATION_COPY.profileSettings}</div>
                            </button>
                        </li>

                        <li className="w-full">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setIsOpen(false)
                                    analytics.trackWithDefaults('profile_dropdown_item_selected', { item: 'rewards' })
                                    navigate(APP_ROUTES.REWARDS)
                                }}
                                className="w-full py-1.5 px-2 flex items-center hover:bg-accent rounded text-sm text-left"
                            >
                                <div className="flex-1 flex justify-start items-center gap-2 min-w-0">
                                    <Gift size={16} />
                                    <div className="truncate">{NAVIGATION_COPY.rewards}</div>
                                </div>
                                {rewardsCount > 0 && (
                                    <span className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-emerald text-white text-xs font-medium">
                                        {rewardsCount}
                                    </span>
                                )}
                            </button>
                        </li>

                        <li className="w-full">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setIsOpen(false)
                                    analytics.trackWithDefaults('profile_dropdown_item_selected', {
                                        item: 'support_and_faqs',
                                    })
                                    navigate(APP_ROUTES.SUPPORT)
                                }}
                                className="w-full py-1.5 px-2 flex items-center gap-2 hover:bg-accent rounded  text-sm text-left"
                            >
                                <MessageCircle size={16} />
                                <div>{NAVIGATION_COPY.supportAndFaqs}</div>
                            </button>
                        </li>

                        <div role="separator" aria-hidden="true" className="w-full h-px bg-muted"></div>

                        <li className="w-full">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => signOutHandler()}
                                className="w-full py-1.5 px-2 flex items-center hover:bg-accent rounded  text-sm  text-left"
                            >
                                <LogOut size={16} className="text-sentiment-bad" />
                                <div className="ml-1 text-sentiment-bad">{NAVIGATION_COPY.logout}</div>
                            </button>
                        </li>
                    </ul>
                </PopoverContent>
            </Popover>

            <AddAccountModal
                open={isNewAccountOpen}
                initialAccountType={newAccountInitialType}
                onClose={() => {
                    setIsNewAccountOpen(false)
                    setNewAccountInitialType(undefined)
                }}
            />
        </>
    )
}
